import { toHearingPhoneMatchKey } from 'features/call/call-hearing/helpers';
import type { VoiceSessionHearingParticipant } from 'features/voice-meeting/interfaces';

// Registry of in-flight `addParticipant` promises keyed by normalized dialed
// phone number. Populated by useHearingConnect at the moment the DAO call is
// fired; consumed by useHearingCancel for cancel-before-connectingId late
// cleanup (no synchronous backend cancel path).
//
// `sessionId` is the backend session the in-flight call targets. Captured at
// register (add-to-existing) or stamped when the new session is created, so
// late cleanup can still deleteParticipant after a forced local session reset
// clears Redux/service session ids.

type InFlightPromise = Promise<VoiceSessionHearingParticipant | null>;

export type InFlightAddParticipant = {
  promise: InFlightPromise;
  sessionId: string;
};

const registry = new Map<string, InFlightAddParticipant>();

const toRegistryKey = (phoneNumber: string) =>
  toHearingPhoneMatchKey(phoneNumber);

export const registerInFlightAddParticipant = (
  phoneNumber: string,
  promise: InFlightPromise | VoiceSessionHearingParticipant | null,
  sessionId = ''
): InFlightAddParticipant => {
  const key = toRegistryKey(phoneNumber);
  const entry: InFlightAddParticipant = {
    promise: Promise.resolve(promise),
    sessionId,
  };
  // Side-effect only — do not create a separate rejecting chain (unhandled rejection).
  entry.promise.then(
    (result) => {
      console.log('HearingDebug: in-flight resolved', {
        phone: key,
        sessionId: entry.sessionId,
        participantId: result?.id ?? null,
      });
    },
    (error: unknown) => {
      console.log('HearingDebug: in-flight rejected', {
        phone: key,
        sessionId: entry.sessionId,
        error,
      });
    }
  );
  registry.set(key, entry);
  console.log('HearingDebug: in-flight registered', {
    phone: key,
    sessionId: entry.sessionId,
    registrySize: registry.size,
  });
  return entry;
};

export const takeInFlightAddParticipant = (
  phoneNumber: string
): InFlightAddParticipant | undefined => {
  const key = toRegistryKey(phoneNumber);
  const entry = registry.get(key);
  if (entry) {
    registry.delete(key);
    console.log('HearingDebug: in-flight taken', {
      phone: key,
      sessionId: entry.sessionId,
      registrySize: registry.size,
    });
  } else {
    console.log('HearingDebug: in-flight take miss', {
      phone: key,
      registrySize: registry.size,
    });
  }
  return entry;
};

export const clearInFlightAddParticipantRegistry = () => {
  registry.clear();
};

// Test alias — prefer clearInFlightAddParticipantRegistry in new code.
export const __clearInFlightAddParticipantRegistry =
  clearInFlightAddParticipantRegistry;
